import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedDeclaration } from "../store/reducers";
import "../components/Signup/Signup.css";
import {
  ChevronDown,
  ArrowUpDown,
  Search,
  ArrowLeft,
  X,
  ChevronsDownUp,
  ChevronsUpDown,
  Columns2,
} from "lucide-react";
import PdfViewer from "../components/Dashboard/PdfViewer";
import { DeclarationSummary } from "../components/DeclarationManagement/DeclarationSummary";
import pdfIcon from "../assets/pdf.png";
import { useLanguage, getDeclarationViewTranslation } from '../hooks/useLanguage';
import apiFetch from "../utils/api";

function DeclarationView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedDeclaration = useSelector((state) => state.selectedDeclaration);
  const groupedTaricJsonOutputs = useSelector((state) => state.groupedTaricJsonOutputs || []);
  const declarationData = selectedDeclaration?.data || null;
  
  // Extract clientes, remitentes, and agentes from taric data
  const getTaricData = () => {
    if (!id) return null;
    const declarationId = typeof id === 'string' ? parseInt(id, 10) : id;
    
    for (const group of groupedTaricJsonOutputs) {
      const exportacion = group.exportaciones?.find(exp => 
        String(exp.id) === String(declarationId)
      );
      if (exportacion && exportacion.taric) {
        return {
          clientes: exportacion.taric.clientes || [],
          remitentes: exportacion.taric.remitentes || [],
          agentes: exportacion.taric.agentes || []
        };
      }
    }
    return null;
  };
  
  const taricData = getTaricData();
  const clientes = taricData?.clientes || [];
  const remitentes = taricData?.remitentes || [];
  const agentes = taricData?.agentes || [];

  // Console logs for debugging
  console.log('=== DeclarationView Debug ===');
  console.log('Current ID from URL:', id);
  console.log('Selected Declaration from Redux:', selectedDeclaration);
  console.log('Declaration Data:', declarationData);
  console.log('Grouped Taric JSON Outputs count:', groupedTaricJsonOutputs.length);
  console.log('Grouped Taric JSON Outputs:', groupedTaricJsonOutputs);

  // State variables
  const [selectedOption, setSelectedOption] = useState("text");
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileUrl, setSelectedFileUrl] = useState(null);
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSpanish] = useLanguage();

  // Load declaration data when component mounts or ID changes
  useEffect(() => {
    if (!id) return;

    const loadDeclaration = async () => {
      // Convert ID to number for comparison (handle both string and number IDs)
      const declarationId = typeof id === 'string' ? parseInt(id, 10) : id;
      
      console.log('Loading declaration with ID:', declarationId);
      console.log('Selected declaration ID:', selectedDeclaration?.id);
      
      // Always check the ID and update JSON in GenerateTab
      // Try to find exportacion in groupedTaricJsonOutputs first
      console.log('Searching in groupedTaricJsonOutputs...');
      let foundExportacion = null;
      let foundGroup = null;
      
      for (const group of groupedTaricJsonOutputs) {
        const exportacion = group.exportaciones?.find(exp => 
          String(exp.id) === String(declarationId)
        );
        if (exportacion) {
          foundExportacion = exportacion;
          foundGroup = group;
          break;
        }
      }
      
      // If we already have the correct declaration in store, use it but still update JSON
      if (selectedDeclaration && String(selectedDeclaration.id) === String(declarationId)) {
        console.log('Using existing selectedDeclaration from Redux');
        console.log('Selected declaration data:', selectedDeclaration.data);
        if (selectedDeclaration.files) {
          setFiles(selectedDeclaration.files);
        }
        
        // Always update taricJson for GenerateTab with current ID
        if (foundExportacion && foundExportacion.taric) {
          const selectedIndex = foundGroup.exportaciones?.findIndex(
            exp => exp.id === foundExportacion.id
          ) ?? 0;
          
          // New structure: use taric object directly
          const taricJson = foundExportacion.taric;
          
          dispatch({ 
            type: 'SET_TARIC_JSON_FROM_GROUPED', 
            payload: {
              taricJson,
              ocr_input_id: foundGroup.ocr_id?.toString(),
              exportacionesIndex: selectedIndex
            }
          });
          
        }
        
        return;
      }
      
      if (foundExportacion && foundExportacion.taric) {
        console.log('Found exportacion in groupedTaricJsonOutputs:', foundExportacion);
        console.log('Found exportacion taric:', foundExportacion.taric);
        
        // Extract the main exportacion data from taric.duas.exportaciones[0]
        const exportacionData = foundExportacion.taric?.duas?.exportaciones?.[0] || {};
        
        // Set selected declaration
        dispatch(setSelectedDeclaration({
          id: foundExportacion.id,
          ocr_id: foundGroup.ocr_id,
          data: [exportacionData],
          files: []
        }));
        
        // Automatically set the taricJson for GenerateTab to display - use API data directly
        const selectedIndex = foundGroup.exportaciones?.findIndex(
          exp => exp.id === foundExportacion.id
        ) ?? 0;
        
        // New structure: use taric object directly
        const taricJson = foundExportacion.taric;
        
        dispatch({ 
          type: 'SET_TARIC_JSON_FROM_GROUPED', 
          payload: {
            taricJson,
            ocr_input_id: foundGroup.ocr_id?.toString(),
            exportacionesIndex: selectedIndex
          }
        });
        
        console.log('=== Auto-loaded Taric JSON for DeclarationView ===');
        console.log('Exportacion ID:', foundExportacion.id);
        console.log('OCR ID:', foundGroup.ocr_id);
        console.log('Selected Index:', selectedIndex);
        console.log('Taric JSON:', taricJson);
        
        return;
      }
      
      console.log('Exportacion not found in groupedTaricJsonOutputs, fetching from API...');

      // If not found in store, fetch from API
      setIsLoading(true);
      try {
        const response = await apiFetch(
          `https://${process.env.REACT_APP_DOCS_API_KEY}/api/items/taric_json_output/`,
          {
            headers: {
              'Content-Type': 'application/json',
              'accept': 'application/json'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          const groups = Array.isArray(data) ? data : [];
          console.log('Fetched taric_json_output from API:', groups.length);
          
          let foundExportacion = null;
          let foundGroup = null;
          
          for (const group of groups) {
            const exportacion = group.exportaciones?.find(exp => 
              String(exp.id) === String(declarationId)
            );
            if (exportacion) {
              foundExportacion = exportacion;
              foundGroup = group;
              break;
            }
          }
          
          if (foundExportacion && foundExportacion.taric) {
            console.log('Found exportacion from API:', foundExportacion);
            console.log('Found exportacion taric:', foundExportacion.taric);
            
            // Extract the main exportacion data from taric.duas.exportaciones[0]
            const exportacionData = foundExportacion.taric?.duas?.exportaciones?.[0] || {};
            
            // Set selected declaration
            dispatch(setSelectedDeclaration({
              id: foundExportacion.id,
              ocr_id: foundGroup.ocr_id,
              data: [exportacionData],
              files: []
            }));
            
            // Automatically set the taricJson for GenerateTab to display - use API data directly
            const selectedIndex = foundGroup.exportaciones?.findIndex(
              exp => exp.id === foundExportacion.id
            ) ?? 0;
            
            // New structure: use taric object directly
            const taricJson = foundExportacion.taric;
            
            dispatch({ 
              type: 'SET_TARIC_JSON_FROM_GROUPED', 
              payload: {
                taricJson,
                ocr_input_id: foundGroup.ocr_id?.toString(),
                exportacionesIndex: selectedIndex
              }
            });
            
            console.log('=== Auto-loaded Taric JSON for DeclarationView (from API) ===');
            console.log('Exportacion ID:', foundExportacion.id);
            console.log('OCR ID:', foundGroup.ocr_id);
            console.log('Selected Index:', selectedIndex);
            console.log('Taric JSON:', taricJson);
          } else {
            console.error('Exportacion not found in API response');
            console.error('Looking for ID:', declarationId);
            navigate('/declaration-management');
          }
        } else {
          console.error('Failed to fetch taric_json_output');
          navigate('/declaration-management');
        }
      } catch (error) {
        console.error('Error loading exportacion:', error);
        navigate('/declaration-management');
      } finally {
        setIsLoading(false);
      }
    };

    loadDeclaration();

    // Cleanup on unmount
    return () => {
      // Don't clear selectedDeclaration here as it might be needed
    };
  }, [id, dispatch, navigate, selectedDeclaration, groupedTaricJsonOutputs]);

  // Update files when selectedDeclaration changes
  useEffect(() => {
    if (selectedDeclaration?.files) {
      console.log('Setting files from selectedDeclaration:', selectedDeclaration.files);
      setFiles(selectedDeclaration.files);
    }
  }, [selectedDeclaration]);

  const closeModal = () => {
    setSelectedOption("text");
  };

  const handleFileClick = (file) => {
    if (selectedFileUrl) {
      URL.revokeObjectURL(selectedFileUrl);
    }
    const fileUrl = URL.createObjectURL(file);
    setSelectedFile(file);
    setSelectedFileUrl(fileUrl);
  };

  // Show loading state or error if no data
  if (isLoading) {
    return (
      <div className="text-left declaration-view">
        <div className="flex items-center justify-center min-h-screen">
          <p>{isSpanish ? 'Cargando...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (!declarationData) {
    return (
      <div className="text-left declaration-view">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="mb-4">{isSpanish ? 'Declaración no encontrada' : 'Declaration not found'}</p>
            <button
              onClick={() => navigate('/declaration-management')}
              className="px-4 py-2 bg-primary text-white rounded-md"
            >
              {isSpanish ? 'Volver a la lista' : 'Back to list'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-left declaration-view">
      <div className="flex gap-2 justify-between items-center">
        <div
          className="flex gap-2 items-center mb-2 w-fit cursor-pointer"
          onClick={() => navigate(`/declaration-management`)}
        >
          <ArrowLeft className="size-7" />
          <h1 className="text-3xl font-bold">{getDeclarationViewTranslation('declarationDetails', isSpanish)}</h1>
        </div>
        <div className="flex gap-1 items-center">
          <button
            className={`flex items-center gap-1 py-2 px-2${
              selectedOption === "split" ? "bg-secondary" : ""
            } rounded-full`}
            title={getDeclarationViewTranslation('expand', isSpanish)}
            onClick={() => {
              setIsSummaryExpanded(!isSummaryExpanded);
            }}
          >
            {isSummaryExpanded ? (
              <ChevronsDownUp className="size-4" />
            ) : (
              <ChevronsUpDown className="size-4" />
            )}
            <span className="text-sm">
              {isSummaryExpanded ? getDeclarationViewTranslation('collapse', isSpanish) : getDeclarationViewTranslation('expand', isSpanish)}
            </span>
          </button>
          <button
            className={`flex items-center gap-2 py-2 bg-secondary px-4${
              selectedOption === "split" ? "bg-secondary" : ""
            } rounded-full`}
            title={getDeclarationViewTranslation('splitView', isSpanish)}
            onClick={() => {
              setSelectedOption("split");
              setIsSummaryExpanded(true);
            }}
          >
            <Columns2 className="size-5" />
            <span className="text-sm">{getDeclarationViewTranslation('splitView', isSpanish)}</span>
          </button>
        </div>
      </div>

      <div className="mt-4">
        {selectedOption === "split" && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-999 py-2 px-3 h-screen flex flex-col gap-2">
            <div
              className="shadow-md flex absolute z-[999] bottom-5 items-center gap-2 p-2 px-4 rounded-full right-7 bg-red-50 cursor-pointer"
              onClick={closeModal}
            >
              <X className="size-4 mt-[2px] stroke-red-500" />
              <p className="text-sm text-red-500">{getDeclarationViewTranslation('close', isSpanish)}</p>
            </div>

            <div className="flex items-center justify-center gap-2 flex-1">
              <div className="bg-white p-4 shadow-lg min-w-[50%] min-h-[100%] overflow-y-auto flex-1 flex flex-col rounded-lg">
                <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                  <h2
                    className="text-lg font-bold truncate"
                    title={getDeclarationViewTranslation('uploadedFiles', isSpanish)}
                  >
                    {getDeclarationViewTranslation('uploadedFiles', isSpanish)} ({files.length})
                  </h2>
                  <div className="relative text-xs">
                    <button
                      className={`w-fit flex gap-2 justify-between items-center`}
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                    >
                      {selectedFile ? (
                        <div
                          className={`truncate border-container px-2 rounded-full cursor-pointer`}
                        >
                          <div className="p-2 px-1 flex gap-2 items-center">
                            <div className="flex gap-1 items-center">
                              <img
                                src={pdfIcon}
                                className="h-4 w-4"
                                alt="PDF"
                              />
                              <span className="truncate max-w-xs">
                                {selectedFile.name}
                              </span>
                            </div>
                            <ChevronDown
                              className={`w-4 h-4 ${
                                dropdownOpen && "rotate-180"
                              }`}
                            />
                          </div>
                        </div>
                      ) : (
                        <div
                          className={`truncate border-container px-2 rounded-full cursor-pointer`}
                        >
                          <div className="p-2 px-1 flex gap-2 items-center">
                            <div className="flex gap-1 items-center">
                              <img
                                src={pdfIcon}
                                className="h-4 w-4"
                                alt="PDF"
                              />
                              <span className="truncate max-w-xs">
                                {getDeclarationViewTranslation('noPdfFileSelected', isSpanish)}
                              </span>
                            </div>
                            <ChevronDown
                              className={`w-4 h-4 ${
                                dropdownOpen && "rotate-180"
                              }`}
                            />
                          </div>
                        </div>
                      )}
                    </button>
                    {dropdownOpen && (
                      <div className="right-0 bg-white shadow-md p-2 border-container rounded-md absolute mt-1 max-h-96 overflow-auto">
                        {files.length > 0 ? (
                          <div className="flex text-xs gap-1.5 flex-col py-1">
                            {files.map((file, index) => (
                              <div
                                key={index}
                                className={`truncate border-container px-2 rounded-full cursor-pointer ${
                                  selectedFile === file
                                    ? "bg-secondary text-white !border-secondary"
                                    : ""
                                }`}
                                onClick={() => handleFileClick(file)}
                              >
                                <div className="p-2 px-1">
                                  <div className="flex gap-1 items-center">
                                    <img
                                      src={pdfIcon}
                                      className="h-4 w-4"
                                      alt="PDF"
                                    />
                                    <span className="truncate max-w-xs">
                                      {file.name}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="bg-gray-100 p-4 rounded text-center text-gray-500">
                            <span>{getDeclarationViewTranslation('noFilesAvailable', isSpanish)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="pdf-viewer flex flex-1 w-full rounded-lg overflow-hidden mt-2">
                  {selectedFileUrl ? (
                    <PdfViewer
                      selectedFileUrl={selectedFileUrl}
                      fileName={selectedFile?.name || ""}
                    />
                  ) : (
                    <div className="min-w-full bg-gray-100 p-8 rounded-lg flex justify-center items-center text-gray-500">
                      <span>{getDeclarationViewTranslation('selectAFileToView', isSpanish)}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-white shadow-lg min-w-[50%] min-h-[100%] max-h-[94vh] overflow-hidden flex-1 rounded-lg">
                <div className="p-4 pt-0 overflow-auto split-declaration-height pb-16">
                  <DeclarationSummary
                    selectedOption={selectedOption}
                    jsonData={declarationData}
                    isSummaryExpanded={isSummaryExpanded}
                    setIsSummaryExpanded={setIsSummaryExpanded}
                    isDirty={isDirty}
                    setIsDirty={setIsDirty}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

<div className="border-container p-4 pt-0 rounded-xl mt-10">
          <DeclarationSummary
            jsonData={declarationData}
            isSummaryExpanded={isSummaryExpanded}
            setIsSummaryExpanded={setIsSummaryExpanded}
            isDirty={isDirty}
            setIsDirty={setIsDirty}
          />
        </div>
        
        {/* Clientes, Remitentes, Agentes Section */}
        {(clientes.length > 0 || remitentes.length > 0 || agentes.length > 0) && (
          <div className="mt-10">
            <h2 className="text-2xl font-semibold mb-4">{isSpanish ? 'Información de Contactos' : 'Contact Information'}</h2>
            <div className="review-container grid gap-4 grid-cols-3">
              {/* Clientes */}
              {clientes.length > 0 && (
                <div className="text-left text-sm flex flex-col border-container p-4 rounded-xl">
                  <h3 className="font-semibold mb-4">{isSpanish ? 'Cliente' : 'Client'}</h3>
                  {clientes.map((cliente, index) => (
                    <div key={cliente.id || index} className="mb-4 last:mb-0">
                      <div className="review-flex flex gap-4">
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'Nombre' : 'Name'}</label>
                          <p className="font-bold">{cliente.nombre || "N/A"}</p>
                        </div>
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'NIF/EORI' : 'NIF/EORI'}</label>
                          <p className="font-bold">{cliente.nif || cliente.eori || cliente.identificacion_fiscal || "N/A"}</p>
                        </div>
                      </div>
                      <div className="review-flex flex gap-4 mt-2">
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'Dirección' : 'Address'}</label>
                          <p className="font-bold">{cliente.direccion || "N/A"}</p>
                        </div>
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'Localidad' : 'City'}</label>
                          <p className="font-bold">{cliente.localidad || "N/A"}</p>
                        </div>
                      </div>
                      <div className="review-flex flex gap-4 mt-2">
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'País' : 'Country'}</label>
                          <p className="font-bold">{cliente.pais || "N/A"}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Remitentes */}
              {remitentes.length > 0 && (
                <div className="text-left text-sm flex flex-col border-container p-4 rounded-xl">
                  <h3 className="font-semibold mb-4">{isSpanish ? 'Remitente' : 'Sender'}</h3>
                  {remitentes.map((remitente, index) => (
                    <div key={remitente.id || index} className="mb-4 last:mb-0">
                      <div className="review-flex flex gap-4">
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'Nombre' : 'Name'}</label>
                          <p className="font-bold">{remitente.nombre || "N/A"}</p>
                        </div>
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'EORI/NIF' : 'EORI/NIF'}</label>
                          <p className="font-bold">{remitente.eori || remitente.identificacion_fiscal || "N/A"}</p>
                        </div>
                      </div>
                      <div className="review-flex flex gap-4 mt-2">
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'Dirección' : 'Address'}</label>
                          <p className="font-bold">{remitente.direccion || "N/A"}</p>
                        </div>
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'Localidad' : 'City'}</label>
                          <p className="font-bold">{remitente.localidad || "N/A"}</p>
                        </div>
                      </div>
                      <div className="review-flex flex gap-4 mt-2">
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'País' : 'Country'}</label>
                          <p className="font-bold">{remitente.pais || "N/A"}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Agentes */}
              {agentes.length > 0 && (
                <div className="text-left text-sm flex flex-col border-container p-4 rounded-xl">
                  <h3 className="font-semibold mb-4">{isSpanish ? 'Agente' : 'Agent'}</h3>
                  {agentes.map((agente, index) => (
                    <div key={agente.id || index} className="mb-4 last:mb-0">
                      <div className="review-flex flex gap-4">
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'Nombre' : 'Name'}</label>
                          <p className="font-bold">{agente.nombre || "N/A"}</p>
                        </div>
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'NIF/EORI' : 'NIF/EORI'}</label>
                          <p className="font-bold">{agente.nif || agente.eori || "N/A"}</p>
                        </div>
                      </div>
                      <div className="review-flex flex gap-4 mt-2">
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'Código' : 'Code'}</label>
                          <p className="font-bold">{agente.codigo || "N/A"}</p>
                        </div>
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'Dirección' : 'Address'}</label>
                          <p className="font-bold">{agente.direccion || "N/A"}</p>
                        </div>
                      </div>
                      <div className="review-flex flex gap-4 mt-2">
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'Localidad' : 'City'}</label>
                          <p className="font-bold">{agente.localidad || "N/A"}</p>
                        </div>
                        <div className="form-group flex-1">
                          <label className="opacity-75 text-xs">{isSpanish ? 'País' : 'Country'}</label>
                          <p className="font-bold">{agente.pais || "N/A"}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
}

export default DeclarationView;
